import { HAZARDS_CONFIG } from '../../../config/hazards.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, VehicleType, type VehicleHazard } from '../../../hazards/HazardDefinition';
import { vehicleSettings, zigzagCrossingAtMeet } from '../../../hazards/VehicleModel';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { hazardSocket, mirroredHazard, mirroredVariant } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type FixedObstaclePlacement, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_CABLE_REEL_40M – "Rollende Kabeltrommel" (user 2026-10-03: "die Kabeltrommeln, die großen, größer machen …
 * und dass sie auf einen zurollen"). The big drum (Ø 1.76 m, VehicleType.CableReel) waits on the road, starts rolling
 * towards the runner and meets the runner at z 16 – straight down an outer lane, or zig-zagging across the whole road,
 * bouncing off the barriers (user 2026-10-05: "zickzack hin und her … wenn sie an die Bande stoßen, sollen sie wieder
 * abgestoßen werden" – VehicleHazard.zigzag). Like the slow truck it is a moving hazard: dodge it (it is too high for a
 * jump); the other lanes carry jumps and slides.
 *
 *   REEL_LEFT          drum lane 0, straight
 *   REEL_ZIGZAG_L      from lane 0 to the right and back – at the left barrier again when it meets the runner
 *   REEL_ZIGZAG_FAST_L faster to and fro – at the right barrier when it meets the runner; not in the warm-up tier
 *   *_RIGHT            mirrored
 * A zig-zag sweeps every lane along its path (no row there); where it is at the meeting line is fixed, so the validator
 * keeps a lane free there (hazardRules).
 */
export const CABLE_REEL_STRAIGHT = {
  length: 40,
  startZ: 34,
  meetZ: 16,
  stopZ: 4,
  /** A zig-zagging drum stops before the R6 row (its path covers every lane). */
  zigzagStopZ: 10,
  rows: { R6: 6, R26: 26, R36: 36 },
} as const;

const W = WORLD_CONFIG;
const ACTIVE_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;
const SOCKET_REEL = 'SOCKET_EVT_CABLE_REEL';

const reel = (id: string, lane: number, zigzag?: { readonly toward: -1 | 1; readonly crossing: number }): VehicleHazard => ({
  id,
  kind: HazardKind.Vehicle,
  vehicle: VehicleType.CableReel,
  socketId: SOCKET_REEL,
  lane,
  startZ: CABLE_REEL_STRAIGHT.startZ,
  meetZ: CABLE_REEL_STRAIGHT.meetZ,
  stopZ: zigzag ? CABLE_REEL_STRAIGHT.zigzagStopZ : CABLE_REEL_STRAIGHT.stopZ,
  ...(zigzag ? { zigzag } : {}),
});

const REEL_L0 = reel('HAZ_REEL_L0', 0);
const REEL_L2 = mirroredHazard(W, 'HAZ_REEL_L2', REEL_L0);
/**
 * Crossing lengths timed so that the drum hits a barrier just as it meets the runner: there it blocks one outer lane, two
 * stay open. Slow: two crossings before (back at its start side, ≈ 8.8 m per crossing); fast: three (the other side, ≈ 5.8 m).
 */
const REEL_SETTINGS = vehicleSettings(HAZARDS_CONFIG, VehicleType.CableReel);
const crossingFor = (bounces: number) => zigzagCrossingAtMeet(0, CABLE_REEL_STRAIGHT.startZ, CABLE_REEL_STRAIGHT.meetZ, bounces, REEL_SETTINGS, W);
const REEL_ZIGZAG_L = reel('HAZ_REEL_ZIGZAG_L', 0, { toward: 1, crossing: crossingFor(2) });
const REEL_ZIGZAG_R = mirroredHazard(W, 'HAZ_REEL_ZIGZAG_R', REEL_ZIGZAG_L);
const REEL_ZIGZAG_FAST_L = reel('HAZ_REEL_ZIGZAG_FAST_L', 0, { toward: 1, crossing: crossingFor(3) });
const REEL_ZIGZAG_FAST_R = mirroredHazard(W, 'HAZ_REEL_ZIGZAG_FAST_R', REEL_ZIGZAG_FAST_L);

const place = (row: keyof typeof CABLE_REEL_STRAIGHT.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({
  spawnPointId: `SOCKET_OBS_${row}_L${lane}`,
  obstacleId,
});

const REEL_LEFT: ObstacleVariant = {
  id: 'REEL_LEFT',
  weight: 1,
  placements: [place('R6', 1, 'OBS_SCAFFOLD_PASS_A'), place('R26', 2, 'OBS_BARRIER_BOARD_A'), place('R36', 1, 'OBS_CONE_ROW_A')],
  hazards: [REEL_L0.id],
};

/**
 * Zig-zag: the R6 row is the only one off its path – a closure in the lane where the drum hits the barrier at the
 * meeting line takes the runner out of it in time.
 */
const REEL_ZIGZAG: ObstacleVariant = {
  id: 'REEL_ZIGZAG_L',
  weight: 1.3,
  placements: [place('R6', 0, 'OBS_LANE_CLOSURE_A')],
  hazards: [REEL_ZIGZAG_L.id],
};

const REEL_ZIGZAG_FAST: ObstacleVariant = {
  id: 'REEL_ZIGZAG_FAST_L',
  weight: 1.1,
  rowLayout: 'dense',
  placements: [place('R6', 2, 'OBS_LANE_CLOSURE_A')],
  hazards: [REEL_ZIGZAG_FAST_L.id],
};
/**
 * Crosswise (user 2026-10-05 late: "kreuzweise"): a second drum starts at the far barrier at the module end and crosses
 * the whole road once against the zig-zagging first one, meeting the runner 15 m later at the same barrier as the first.
 * Two drums at the same meeting line would close every lane – staggered, the open lane is the same at both lines.
 * The fast pair only with dense rows.
 */
const CROSS = { startZ: 38, meetZ: 31 } as const;
const crossReel = (id: string, lane: 0 | 2): VehicleHazard => ({
  ...reel(id, lane, { toward: lane === 0 ? 1 : -1, crossing: zigzagCrossingAtMeet(0, CROSS.startZ, CROSS.meetZ, 1, REEL_SETTINGS, W) }),
  startZ: CROSS.startZ,
  meetZ: CROSS.meetZ,
});
const REEL_CROSS_2_R = crossReel('HAZ_REEL_CROSS_2_R', 2);
const REEL_CROSS_2_L = crossReel('HAZ_REEL_CROSS_2_L', 0);
const REEL_CROSS: ObstacleVariant = {
  id: 'REEL_CROSS_L',
  weight: 1.2,
  placements: [place('R6', 0, 'OBS_LANE_CLOSURE_A')],
  hazards: [REEL_ZIGZAG_L.id, REEL_CROSS_2_R.id],
};
const REEL_CROSS_FAST: ObstacleVariant = {
  id: 'REEL_CROSS_FAST_L',
  weight: 1,
  rowLayout: 'dense',
  placements: [place('R6', 2, 'OBS_LANE_CLOSURE_A')],
  hazards: [REEL_ZIGZAG_FAST_L.id, REEL_CROSS_2_L.id],
};
const SIDES = moduleSides(CABLE_REEL_STRAIGHT.length);

export const SEG_CABLE_REEL_40M: SegmentDefinition = {
  id: 'SEG_CABLE_REEL_40M',
  kind: SegmentKind.Straight,
  displayName: 'Rollende Kabeltrommel 40 m',
  length: CABLE_REEL_STRAIGHT.length,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }],

  entry: entryConnector(),
  exits: [straightExit(CABLE_REEL_STRAIGHT.length)],
  lanes: standardLanes(W),

  obstacleSpawnPoints: Object.entries(CABLE_REEL_STRAIGHT.rows).flatMap(([row, z]) => obstacleRow(W, row, z, ACTIVE_TYPES)),
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    REEL_LEFT,
    mirroredVariant(W, 'REEL_RIGHT', REEL_LEFT, [REEL_L2.id]),
    REEL_ZIGZAG,
    mirroredVariant(W, 'REEL_ZIGZAG_R', REEL_ZIGZAG, [REEL_ZIGZAG_R.id]),
    REEL_ZIGZAG_FAST,
    mirroredVariant(W, 'REEL_ZIGZAG_FAST_R', REEL_ZIGZAG_FAST, [REEL_ZIGZAG_FAST_R.id]),
    REEL_CROSS,
    mirroredVariant(W, 'REEL_CROSS_R', REEL_CROSS, [REEL_ZIGZAG_R.id, REEL_CROSS_2_L.id]),
    REEL_CROSS_FAST,
    mirroredVariant(W, 'REEL_CROSS_FAST_R', REEL_CROSS_FAST, [REEL_ZIGZAG_FAST_R.id, REEL_CROSS_2_R.id]),
  ],
  hazards: [REEL_L0, REEL_L2, REEL_ZIGZAG_L, REEL_ZIGZAG_R, REEL_ZIGZAG_FAST_L, REEL_ZIGZAG_FAST_R, REEL_CROSS_2_L, REEL_CROSS_2_R],

  collectibleSpawnPoints: collectibleLanes(W, 1, CABLE_REEL_STRAIGHT.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  ...SIDES,
  dynamicEventSockets: [...SIDES.dynamicEventSockets, hazardSocket(SOCKET_REEL, 0, CABLE_REEL_STRAIGHT.meetZ, DynamicEventType.ForkliftCrossing)],

  generation: {
    // user 2026-10-03: should be found and come often ("die Rollen können öfter kommen") – own family, from 300 m
    weight: 0.6, // was 0.35 – more drums against the denser cranes (user 2026-10-05 late)
    minDistance: 200,
    minSegmentsAfterJunction: 1,
    family: 'CABLE_REEL',
    // user 2026-10-04: "nach 900 bis 1000 m die Kabelrollen auf einen zurollen lassen im Vier- oder Fünferpaar, so dass
    // man geschickt durchmanövrieren muss" – single drums first, from 900 m often a whole series (one drum per 40 m
    // module, each lane picked anew, jumps and slides in the other lanes – dodge after dodge)
    chain: {
      lengths: [[1, 1]],
      // user 2026-10-05: "auch mal zwei oder drei hintereinander" – short series too, and from 3000 m longer ones
      // user 2026-10-05 late: "drei, vier oder fünf hintereinander … quer über die Fahrbahn und kreuzweise" – series of 3–5
      from: [
        { distance: 400, lengths: [[2, 0.4], [3, 0.6]] },
        { distance: 900, lengths: [[3, 0.4], [4, 0.35], [5, 0.25]] },
        { distance: 2500, lengths: [[3, 0.2], [4, 0.4], [5, 0.4]] },
      ],
    },
    tags: ['straight', 'obstacles', 'mixed', 'hazard', 'vehicle', 'cable'],
  },
};
